import React, {useState} from "react";
import {Modal as BootStrapModal} from "react-bootstrap";
import styles from "./ConfirmationModal.module.css";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheckCircle, faCopy, faListOl, faFileDownload} from "@fortawesome/free-solid-svg-icons";
import axiosInstance from "../../../AxiosInstance";

export default function ConfirmationModal({show, data, onClose}) {
    const [downloading, setDownloading] = useState(false);

    if (!data) return null;

    const bankInfo = data.bank_info;
    const invoice = data.invoice;
    const nextSteps = data.next_steps || [];

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    const downloadInvoice = async () => {
        if (!invoice?.id) return;

        setDownloading(true);
        try {
            const response = await axiosInstance.get(`billing/invoices/${invoice.id}/download/`, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `invoice_${invoice.invoice_number || invoice.id}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading invoice:', err);
            // Fallback: open in new tab
            window.open(`${axiosInstance.defaults.baseURL}billing/invoices/${invoice.id}/download/`, '_blank');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <BootStrapModal show={show} onHide={onClose} centered size="lg">
            <div className={styles.modal}>
                <div className={styles.header}>
                    <div className={styles.successIcon}>
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                    <h2 className={styles.title}>Request Registered!</h2>
                    <p className={styles.subtitle}>Your subscription request has been successfully registered</p>
                </div>

                <div className={styles.body}>
                    <div className={styles.section}>
                        <h3 className={styles.sectionTitle}>Make your transfer</h3>

                        {bankInfo && (
                            <div className={styles.transferDetails}>
                                <div className={styles.transferRow}>
                                    <span className={styles.label}>Amount</span>
                                    <div className={styles.valueWrapper}>
                                        <span className={styles.amountValue}>${bankInfo.amount?.toFixed(2)} USD</span>
                                        <button
                                            className={styles.copyButton}
                                            onClick={() => copyToClipboard(bankInfo.amount?.toFixed(2))}
                                            title="Copy"
                                        >
                                            <FontAwesomeIcon icon={faCopy} />
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.transferRow}>
                                    <span className={styles.label}>Reference</span>
                                    <div className={styles.valueWrapper}>
                                        <span className={styles.value}>{bankInfo.concept}</span>
                                        <button
                                            className={styles.copyButton}
                                            onClick={() => copyToClipboard(bankInfo.concept)}
                                            title="Copy"
                                        >
                                            <FontAwesomeIcon icon={faCopy} />
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.transferRow}>
                                    <span className={styles.label}>USD Account</span>
                                    <div className={styles.valueWrapper}>
                                        <span className={styles.value}>{bankInfo.account_usd}</span>
                                        <button
                                            className={styles.copyButton}
                                            onClick={() => copyToClipboard(bankInfo.account_usd)}
                                            title="Copy"
                                        >
                                            <FontAwesomeIcon icon={faCopy} />
                                        </button>
                                    </div>
                                </div>

                                <div className={styles.transferRow}>
                                    <span className={styles.label}>Bank</span>
                                    <span className={styles.value}>{bankInfo.bank}</span>
                                </div>

                                <div className={styles.transferRow}>
                                    <span className={styles.label}>SWIFT</span>
                                    <div className={styles.valueWrapper}>
                                        <span className={styles.value}>{bankInfo.swift}</span>
                                        <button
                                            className={styles.copyButton}
                                            onClick={() => copyToClipboard(bankInfo.swift)}
                                            title="Copy"
                                        >
                                            <FontAwesomeIcon icon={faCopy} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {nextSteps.length > 0 && (
                        <div className={styles.section}>
                            <div className={styles.stepsHeader}>
                                <FontAwesomeIcon icon={faListOl} className={styles.stepsIcon} />
                                <h3 className={styles.sectionTitle}>Next steps</h3>
                            </div>

                            <ol className={styles.stepsList}>
                                {nextSteps.map((step, index) => (
                                    <li key={index} className={styles.stepItem}>
                                        <span className={styles.stepNumber}>{index + 1}</span>
                                        <span className={styles.stepText}>{step}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}

                    {invoice && (
                        <div className={styles.section}>
                            <h3 className={styles.sectionTitle}>Invoice generated</h3>
                            <div className={styles.invoiceInfo}>
                                <div className={styles.invoiceRow}>
                                    <span className={styles.label}>Invoice #</span>
                                    <span className={styles.value}>{invoice.invoice_number || `#${invoice.id}`}</span>
                                </div>
                                {invoice.amount_with_tax && (
                                    <div className={styles.invoiceRow}>
                                        <span className={styles.label}>Total (inc. IGV 18%)</span>
                                        <span className={styles.amountValue}>${parseFloat(invoice.amount_with_tax).toFixed(2)} USD</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className={styles.notice}>
                        <p>Save this information to complete the transfer. Once payment is confirmed, your subscription will be activated.</p>
                    </div>
                </div>

                <div className={styles.footer}>
                    {invoice && (
                        <button
                            className={styles.downloadButton}
                            onClick={downloadInvoice}
                            disabled={downloading}
                        >
                            <FontAwesomeIcon icon={faFileDownload} />
                            {downloading ? 'Downloading...' : 'Download Invoice'}
                        </button>
                    )}
                    <button className={styles.closeButton} onClick={onClose}>
                        Got it
                    </button>
                </div>
            </div>
        </BootStrapModal>
    );
}
